/**
 * App Center - Main Component (Ant Design v5)
 * 
 * Complete app management interface with:
 * - Overview tab with stats
 * - My Apps management
 * - Build new apps
 * - Drafts
 */

import React, { useEffect } from 'react';
import { Card, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  AppstoreOutlined,
  ShopOutlined,
  FileTextOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  SafetyOutlined
} from '@ant-design/icons';

// Tab components
import OverviewTab from './tabs/OverviewTab';
import InstalledTab from './tabs/InstalledTab';
import MarketplaceTab from './tabs/MarketplaceTab';
import CreateTab from './tabs/CreateTab';
import DraftsTab from './tabs/DraftsTab';
import SecurityReviewTab from './tabs/SecurityReviewTab';

// Navigation utilities and hooks
import { TabName, setGlobalNavigate } from './utils/navigation';
import { useNavigation, useKeyboardShortcuts } from './hooks/useNavigation';

interface PluginManagementHubProps {
  tab?: TabName;
}

const PluginManagementHub: React.FC<PluginManagementHubProps> = ({ tab }) => {
  const navigate = useNavigate();
  const { activeTab, navigateTo } = useNavigation(tab);
  
  // Set global navigate function for navigateToTab utility
  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  // Setup keyboard shortcuts (Alt+1-5, Alt+Left/Right)
  useKeyboardShortcuts(navigateTo);

  const handleTabChange = (key: string) => {
    navigateTo(key as TabName);
  };

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <AppstoreOutlined />
          <span style={{ marginLeft: 8 }}>Overview</span>
        </span>
      ),
      children: <OverviewTab />
    },
    {
      key: 'installed',
      label: (
        <span>
          <ThunderboltOutlined />
          <span style={{ marginLeft: 8 }}>My Apps</span>
        </span>
      ),
      children: <InstalledTab />
    },
    {
      key: 'marketplace',
      label: (
        <span>
          <ShopOutlined />
          <span style={{ marginLeft: 8 }}>Apps Library</span>
        </span>
      ),
      children: <MarketplaceTab />
    },
    {
      key: 'create',
      label: (
        <span>
          <RocketOutlined />
          <span style={{ marginLeft: 8 }}>Create New</span>
        </span>
      ),
      children: <CreateTab />
    },
    {
      key: 'drafts',
      label: (
        <span>
          <FileTextOutlined />
          <span style={{ marginLeft: 8 }}>Drafts</span>
        </span>
      ),
      children: <DraftsTab />
    },
    {
      key: 'security',
      label: (
        <span>
          <SafetyOutlined />
          <span style={{ marginLeft: 8 }}>Security Review</span>
        </span>
      ),
      children: <SecurityReviewTab />
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          size="large"
          animated
          style={{
            '--active-tab-bg': '#1890ff',
            '--active-tab-color': '#fff',
          } as React.CSSProperties}
          className="plugin-manager-tabs"
        />
        <style>{`
          .plugin-manager-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
            color: white;
            padding: 8px 20px;
            border-radius: 6px;
            font-weight: 600;
          }
          .plugin-manager-tabs .ant-tabs-tab {
            margin: 0 4px;
          }
          .plugin-manager-tabs .ant-tabs-tab-btn {
            padding: 8px 16px;
            border-radius: 6px;
            transition: all 0.3s ease;
          }
          .plugin-manager-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn {
            background: rgba(24, 144, 255, 0.1);
            color: #1890ff;
          }
          .plugin-manager-tabs .ant-tabs-ink-bar {
            display: none;
          }
        `}</style>
      </Card>
    </div>
  );
};

export default PluginManagementHub;
